import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_BackgroundsAdvanced = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">🖼️ Advanced CSS: Multiple Backgrounds</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS allows you to apply <b>multiple background images</b> to a single
        element. You can layer them using <code>background-image</code>, and
        control each one individually with <code>background-size</code>,{" "}
        <code>background-position</code>, and <code>background-repeat</code>.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Multiple backgrounds are declared as <b>comma-separated lists</b>.
          </li>
          <li>
            The first background is placed <b>on top</b>, and the last one is
            placed <b>at the bottom</b>.
          </li>
          <li>
            Each background can have its own <code>size, position, repeat</code>
            , etc.
          </li>
          <li>
            Useful for adding <b>icons, overlays, patterns</b> with a main
            background image.
          </li>
          <li>
            Combine with <code>background-blend-mode</code> for creative layered
            effects.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Multiple background syntax */
background-image: url("top.png"), url("middle.png"), url("bottom.jpg");
background-position: top left, center, bottom right;
background-repeat: no-repeat, repeat-x, no-repeat;
background-size: 50px 50px, cover, contain;`}
        />
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Advanced Examples</h2>

        {/* Example 1 */}
        <h3 className="section-subtitle">1. Icon Overlay on a Banner</h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("icon.png"), url("banner.jpg");
  background-position: top left, center;
  background-repeat: no-repeat, no-repeat;
  background-size: 60px 60px, cover;
}`}
        />
        <div
          style={{
            width: "320px",
            height: "180px",
            borderRadius: "10px",
            border: "1px solid #ccc",
            backgroundImage:
              "url('https://picsum.photos/60/60'), url('https://picsum.photos/400/300')",
            backgroundPosition: "top left, center",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundSize: "60px 60px, cover",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 2 */}
        <h3 className="section-subtitle">2. Pattern + Main Image</h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("pattern.png"), url("landscape.jpg");
  background-size: 40px 40px, cover;
  background-repeat: repeat, no-repeat;
  background-position: center, center;
}`}
        />
        <div
          style={{
            width: "320px",
            height: "180px",
            borderRadius: "10px",
            border: "1px solid #ccc",
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png'), url('https://picsum.photos/400/250')",
            backgroundSize: "40px 40px, cover",
            backgroundRepeat: "repeat, no-repeat",
            backgroundPosition: "center, center",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 3 */}
        <h3 className="section-subtitle">3. Gradient Overlay + Image</h3>
        <CodeCopyBlock
          code={`div {
  background-image: linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url("photo.jpg");
  background-size: cover, cover;
  background-position: center, center;
  background-repeat: no-repeat, no-repeat;
}`}
        />
        <div
          style={{
            width: "320px",
            height: "180px",
            borderRadius: "10px",
            border: "1px solid #ccc",
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1400&q=80')",
            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
            backgroundRepeat: "no-repeat, no-repeat",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            textShadow: "1px 1px 4px rgba(0,0,0,0.6)",
            marginTop: "0.5rem",
          }}
        >
          🌄 Gradient + Image
        </div>

        {/* Example 4 */}
        <h3 className="section-subtitle">4. Decorative Corners</h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("corner.png"), url("corner.png");
  background-position: top left, bottom right;
  background-repeat: no-repeat, no-repeat;
  background-size: 40px 40px, 40px 40px;
  background-color: #f0f9ff;
}`}
        />
        <div
          style={{
            width: "320px",
            height: "150px",
            borderRadius: "10px",
            border: "1px solid #ccc",
            backgroundColor: "#f0f9ff",
            backgroundImage:
              "url('https://picsum.photos/40/40?random=1'), url('https://picsum.photos/40/40?random=2')",
            backgroundPosition: "top left, bottom right",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundSize: "40px 40px, 40px 40px",
            marginTop: "0.5rem",
          }}
        />
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you know how to layer multiple backgrounds, explore{" "}
          <a href="/css/background-blend-mode" className="learn-more">
            CSS Background Blend Modes
          </a>{" "}
          to create modern visual effects.
        </p>
      </div>
    </div>
  );
};

export default CSS_BackgroundsAdvanced;
